/** @jsxImportSource @emotion/react */
'use memo'

import { EnvironmentOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistrationVenue } from '@sportninja/common/features/organization/registration/types'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'

interface Props {
  venue: OrgRegistrationVenue
  /** Adds the city and region under the name ("Cheektowaga, NY"). */
  withPlace?: boolean
}

/**
 * A venue's pin and name, opening it on the map in a new tab. The full address
 * shows on hover and is read out with the name. Shared by the card and the
 * list view.
 */
const OrgRegistrationVenueLink = ({ venue, withPlace = false }: Props) => {
  'use memo'

  const where = [venue.name, venue.address].filter(Boolean).join(', ')

  return (
    <Link
      href={venue.mapUrl}
      target='_blank'
      rel='noopener noreferrer'
      title={venue.address || undefined}
      aria-label={`${where} (opens map in a new tab)`}
    >
      <Pin aria-hidden='true'>
        <EnvironmentOutlined />
      </Pin>
      <Text>
        <Name>{venue.name}</Name>
        {withPlace && venue.place ? <Place>{venue.place}</Place> : null}
      </Text>
    </Link>
  )
}

export default OrgRegistrationVenueLink

const Link = styled.a`
  ${paragraphSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  line-height: 1.3;
  color: ${colors.WHITE};

  &:hover,
  &:focus-visible {
    color: ${colors.WHITE};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`

// 16px, level with the name's first line.
const Pin = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 16px;
  margin-top: 1px;
  color: ${colors.NEUTRAL_50};
`

const Text = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const Name = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const Place = styled.span`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
`
